'use client';

import React from 'react';
import { useTranslations } from 'next-intl';
import { CustomImage } from 'components/shared/custom-image/CustomImage';
import { PLCTA } from 'components/performance-lab/PLCTA';
import { FESTIVE_CARD_ICONS } from '../festiveAssets';
import { festiveCheckoutPath } from '../festiveOffer';
import { formatInr, type FestiveCardItem } from '../useFestivePlanItems';
import { useOpenFestivePlanDetail } from './useOpenFestivePlanDetail';
import { useOpenFestiveCompare } from './useOpenFestiveCompare';
import styles from './FestiveGiftCard.module.css';

/**
 * Frosted plan card from Figma Performance Lab Web 5225:8857 (thumbnail + divider)
 * and 5225:9166 (plain). `wide` is the 2-up recovery row (5225:9242).
 */
export const FestiveGiftCard: React.FC<{
  item: FestiveCardItem;
  /** `feature` = the full-width card: title left, details stacked right. */
  variant: 'thumb' | 'plain' | 'feature';
  wide?: boolean;
  /** V2 type treatment: Geist / Geist Mono, sentence-case buttons (Figma 5324:2207). */
  type?: 'geist';
}> = ({ item, variant, wide = false, type }) => {
  const t = useTranslations('performance-lab');
  const openDetail = useOpenFestivePlanDetail();
  const openCompare = useOpenFestiveCompare();
  const { name, description, price, strikePrice, multiple, id } = item;
  /** Diagnostics Learn more → shared compare modal; recovery/IV → plan detail. */
  const showLearnMore =
    type === 'geist' &&
    (item.panel === 'diagnostics' ||
      item.panel === 'recovery' ||
      item.panel === 'iv');
  const showSpec = type === 'geist' && Boolean(item.spec) && id.startsWith('iv-');
  const paragraphs = description.split('\n\n').map((part) => part.trim()).filter(Boolean);
  const iconSrc = FESTIVE_CARD_ICONS[id];
  const giftTitle =
    type === 'geist'
      ? t('festive.lineup.giftThis')
      : t('festive.plans.groups.longevity.cta');
  const onLearnMore = () => {
    if (item.panel === 'diagnostics') {
      openCompare('diagnostics');
      return;
    }
    openDetail(item, giftTitle);
  };
  const href = festiveCheckoutPath(id);
  const priceText = multiple
    ? t('festive.plans.priceFrom', { price: formatInr(price) })
    : formatInr(price);
  const priceAnnounced = strikePrice
    ? t(multiple ? 'festive.plans.priceFromWasNow' : 'festive.plans.priceWasNow', {
        price: formatInr(price),
        now: formatInr(price),
        was: formatInr(strikePrice),
      })
    : priceText;

  const saveTag = null;
  const priceRow = (
    <div className={styles.priceRow}>
      <span className={styles.price} aria-hidden="true">{priceText}</span>
      {strikePrice ? (
        <s className={styles.strike} aria-hidden="true">{formatInr(strikePrice)}</s>
      ) : null}
      <span className={styles.visuallyHidden}>{priceAnnounced}</span>
    </div>
  );
  // V1–V3 price block: price with the solid "Save 10%" tag beside it, the original price
  // struck through on the line below.
  const priceBlock = (
    <div className={styles.priceBlock}>
      <div className={styles.priceLine}>
        <span className={styles.price} aria-hidden="true">{priceText}</span>
      </div>
      {strikePrice ? (
        <s className={styles.strike} aria-hidden="true">{formatInr(strikePrice)}</s>
      ) : null}
      <span className={styles.visuallyHidden}>{priceAnnounced}</span>
    </div>
  );
  // PLCTA wraps the pill in a link; the link is the one tab stop and carries the name.
  const cta = (
    <PLCTA
      href={href}
      linkProps={{
        className: styles.ctaLink,
        'aria-label': t('festive.plans.ctaAria', { plan: name }),
      }}
      $plVariant="primary"
      className={
        type === 'geist' ? `${styles.cta} pl-custom-type` : styles.cta
      }
      tabIndex={-1}
      aria-hidden="true"
      data-buttontype={`performance_lab_festive_gift_${id}`}
    >
      {giftTitle}
    </PLCTA>
  );
  const learnMore = showLearnMore ? (
    <button
      type="button"
      className={`${styles.learnMore} pl-inline-link`}
      aria-label={t('festive.versions.plans.learnMoreAria', { plan: name })}
      data-buttontype={`performance_lab_festive_learn_more_${id}`}
      onClick={onLearnMore}
    >
      {t('festive.versions.plans.learnMore')}
    </button>
  ) : null;
  const descriptionBody =
    paragraphs.length > 1 ? (
      <div className={styles.descriptionStack}>
        {paragraphs.map((paragraph, index) => (
          <p className={styles.description} key={paragraph}>
            {paragraph}
            {learnMore && index === paragraphs.length - 1 ? (
              <> {learnMore}</>
            ) : null}
          </p>
        ))}
      </div>
    ) : (
      <p className={styles.description}>
        {paragraphs[0] ?? ''}
        {learnMore ? <> {learnMore}</> : null}
      </p>
    );

  // V1–V3 Max card: one left column — label, name, description — with the price block
  // and CTA side by side at the bottom.
  if (variant === 'feature' && type === 'geist') {
    return (
      <article
        className={styles.card}
        data-variant="feature"
        data-type={type}
        data-plan-id={id}
      >
        <div className={styles.featureIntro}>
          <h3 className={`${styles.name} ${styles.featureName}`}>{name}</h3>
          {descriptionBody}
        </div>
        <div className={styles.featureBody}>
          {priceBlock}
          {cta}
        </div>
      </article>
    );
  }

  if (variant === 'feature') {
    return (
      <article
        className={styles.card}
        data-variant="feature"
        data-type={type}
        data-plan-id={id}
      >
        <div className={styles.featureTitle}>
          <h3 className={`${styles.name} ${styles.featureName}`}>{name}</h3>
        </div>
        <div className={styles.featureBody}>
          {descriptionBody}
          {saveTag}
          {priceRow}
          {cta}
        </div>
      </article>
    );
  }

  const title = <h3 className={styles.name}>{name}</h3>;

  return (
    <article
      className={styles.card}
      data-variant={variant}
      data-type={type}
      data-wide={wide ? 'true' : undefined}
      data-detail={showLearnMore ? 'true' : undefined}
      data-spec={showSpec ? 'true' : undefined}
      data-plan-id={id}
    >
      <div className={styles.top}>
        {variant === 'thumb' ? (
          <div className={styles.header}>
            {iconSrc ? (
              <span className={styles.thumb}>
                <span className={styles.thumbImage}>
                  <CustomImage src={iconSrc} alt="" fill sizes="56px" />
                </span>
              </span>
            ) : null}
            <div className={showSpec ? `${styles.headerCopy} ${styles.headerCopySpec}` : styles.headerCopy}>
              {title}
              {showSpec ? (
                <div className={styles.specTag}>
                  <p>{item.spec}</p>
                </div>
              ) : null}
            </div>
          </div>
        ) : (
          title
        )}
        {descriptionBody}
      </div>

      {variant === 'thumb' && (
        <span className={styles.divider} aria-hidden="true" />
      )}

      <div className={styles.footerBlock}>
        {variant === 'thumb' && type !== 'geist' && saveTag}
        <div className={styles.footer}>
          {type === 'geist' ? priceBlock : priceRow}
          {cta}
        </div>
      </div>
    </article>
  );
};
